/*
 * Copyright (c) 2024. Tooling Team
 */

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { getSelectedObject } from "../utils/funcs";
import { LAB_DOMAIN_VALUE } from "../utils/constants";

export default function AttributeFilter(props) {
  const [selectedItem, setSelectedItem] = useState(null);
  const appliedCloneTo = useRef({ value: undefined, applied: false });
  const dropdownValues = useMemo(() => {
    const values =
      props.filter.name === "Client" && props.clientOptions
        ? props.clientOptions
        : props.filter.values;
    const filteredValues =
      props.filter.name === "Standard_Code_System" &&
      props.selectedDomain?.toLowerCase() === LAB_DOMAIN_VALUE
        ? values?.filter((item) => item.label?.toLowerCase() !== "all")
        : values;

    return Array.isArray(filteredValues) ? filteredValues : [];
  }, [
    props.clientOptions,
    props.filter.name,
    props.filter.values,
    props.selectedDomain,
  ]);

  const handleFilterChange = (event) => {
    const curObject = getSelectedObject(event, dropdownValues);
    setSelectedItem(curObject);
    props.onFilterChange(curObject, props.filter);
  };

  useEffect(() => {
    setSelectedItem((currentSelection) => {
      if (!currentSelection) return currentSelection;
      return dropdownValues.some(
        (item) => item.value === currentSelection.value
      )
        ? currentSelection
        : null;
    });
  }, [dropdownValues]);

  // On Clone success, update the search criteria to the CloneTo map
  //but only for the first search- otherwise it'll be too sticky
  useEffect(() => {
    const cloneToValue = props.cloneTo?.state?.[props.filter.name];

    if (appliedCloneTo.current.value !== cloneToValue) {
      appliedCloneTo.current = { value: cloneToValue, applied: false };
    }

    if (cloneToValue && !appliedCloneTo.current.applied) {
      const cloneToItem = dropdownValues.find(
        (item) => item.value === cloneToValue
      );

      if (cloneToItem) {
        setSelectedItem(cloneToItem);
        appliedCloneTo.current.applied = true;
      }
    }
  }, [props.cloneTo?.state, props.filter.name, dropdownValues]);

  useEffect(() => {
    const name = props.newMapLocation?.state?.[props.filter.name];
    if (!name) return;

    const event = dropdownValues.find((item) => item.value === name);
    if (event && selectedItem?.value !== event.value) {
      setSelectedItem(event);
      props.onFilterChange(event, props.filter);
    }
  }, [
    dropdownValues,
    props.filter,
    props.newMapLocation,
    props.onFilterChange,
    selectedItem?.value,
  ]);

  return (
    <div className="col ms-3">
      <label htmlFor={props.filter.name} className="fw-bold">
        {props.filter.name.replaceAll("_", " ")}
      </label>
      <SelectInput
        key={`${props.filter.name}-${selectedItem?.value ?? "empty"}`}
        placeholder="Select..."
        isSearchable
        isDisabled={props.isSearchDisabled || props.filter.disabled}
        value={selectedItem ? selectedItem.value : null}
        onChange={(event) => handleFilterChange(event)}
        options={dropdownValues}
        id={props.filter.name}
        aria-label={props.filter.name}
      />
    </div>
  );
}

AttributeFilter.propTypes = {
  isSearchDisabled: PropTypes.bool.isRequired,
  filter: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  newMapLocation: PropTypes.object,
  cloneTo: PropTypes.object,
  clientOptions: PropTypes.array,
  selectedDomain: PropTypes.string,
};
